import { Trans } from '@lingui/macro'

import * as React from 'react'

import { TFProductCategory } from '~graphql/generated/clientGraphql'
import { isDefined } from '~logic/helpers/isDefined'
import { NextImage } from '~shared/components/Images/NextImage'
import { menuScreenElementIds } from '~shared/config/menuScreenElementIds'
import { WithProductCountBadge } from '~ui/components/badges/WithProductCountBadge'
import { Stack } from '~ui/components/layout/Stack'
import { WareCategory } from '~ui/components/ware/WareCategory/WareCategory'
import { WareItem } from '~ui/components/ware/WareItem/WareItem'
import { TSingleSpace } from '~ui/styleGuide/spaces'

import { SoldOutBadge } from '../../../components/Badges/SoldOutBadge'
import { CustomerMessage } from '../../../components/Messages/CustomerMessage'
import { useProductCountGetter } from '../../Cart'
import { useProductDetailControlCtx } from '../../ProductDetail/contexts/ProductDetailProvider'
import { useProductListCountCtx } from '../contexts/ProductListDataProvider'
import { getProductListPlaceholders } from '../helpers/getProductListPlaceholders'
import { ProductPrice } from './ProductPrice'

const placeholderItemCounts = [4, 3, 4]

// TODO separate empty message (list + promo header)

type TCategories = RoA<TFProductCategory>

type TProps = NoChildren & {
  isMobile?: boolean
  productCategories?: Nullable<TCategories>
  searchValue?: string
  productPadding: TSingleSpace
}

// to synchronize category heading padding with padding of products in the list
const { productListContainerId } = menuScreenElementIds

/**
 * Product categories rendered into a Stack.
 */
export const ProductList: React.FC<TProps> = ({
  isMobile,
  productCategories,
  searchValue,
  productPadding,
}) => {
  const productCategoryCount = useProductListCountCtx()
  const { openProductDetail } = useProductDetailControlCtx()
  const getProductCount = useProductCountGetter()

  // create placeholder categories with products mapped to the shape of the real data
  const placeholderCategories = getProductListPlaceholders(placeholderItemCounts)

  const categories = isDefined(productCategories) ? productCategories : placeholderCategories

  const padding: TSingleSpace = isMobile ? productPadding : `none`

  const showSkeleton = !productCategories

  const getCustomerMessage = () => {
    if (productCategoryCount === 0) {
      return <Trans>We are sorry, products are currently unavailable</Trans>
    }

    if (searchValue) {
      return <Trans>Nothing found for "{searchValue}"</Trans>
    }

    return ``
  }

  if (categories.length === 0) {
    return <CustomerMessage message={getCustomerMessage()} />
  }

  // return real components (the data is ready) OR loading skeletons (there is NO data)
  return (
    <Stack padding={`none`} spacing={`extraLarge`} id={productListContainerId}>
      {categories.map(({ id, nameLabel, descriptionLabel, products }) => (
        <WareCategory
          id={id}
          key={id}
          title={nameLabel}
          descriptionLabel={descriptionLabel}
          leftPadding={padding}
          rightPadding={padding}
          showSkeleton={showSkeleton}
        >
          {products.map((product) => (
            <WithProductCountBadge
              size={`sm`}
              productCount={getProductCount(product.id)}
              productRightPadding={padding}
              key={product.id}
            >
              <WareItem
                allergenLabel={<Trans>allergens</Trans>}
                leftPadding={padding}
                rightPadding={padding}
                showSkeleton={showSkeleton}
                onClick={() => openProductDetail({ product, categoryId: id, promoTag: null })}
                customImageElement={
                  product.image ? <NextImage src={product.image} layout={`fill`} /> : null
                }
                priceBadge={product.isSoldOut ? <SoldOutBadge /> : null}
                opacity={product.isSoldOut ? 0.5 : 1}
                item={{
                  imageUrl: product.image,
                  nameLabel: product.nameLabel,
                  description: product.descriptionLabel ?? ``,
                  allergens: product.allergens,
                  price: (
                    <ProductPrice
                      formattedPrice={product.price.formattedValue}
                      isCombinationMeal={product.isCombinationMeal}
                    />
                  ),
                }}
              />
            </WithProductCountBadge>
          ))}
        </WareCategory>
      ))}
    </Stack>
  )
}
